// ═══════════════════════════════════════════════════════════════
// Out of Cycle (OOC) - MobX Store
// ═══════════════════════════════════════════════════════════════

import {
  OOCSubmission,
  OOCForecastAdjustment,
  OOCNDCSearchResult,
  OOCIntegrationSummary,
  OOCDashboardCounts,
  OOCStatus,
  OOCReasonType,
  OOCTabType,
  OOCDateFilter,
  OOCValidationResult,
  ProbabilityLevel,
  OOCProvForecastData,
} from 'Interfaces/FDPInterfaces/oocInterfaces';
import { SelectedPackSize } from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { generate15MonthPeriods } from 'data/fdp/oocMockData';
import { oocService } from 'services/fdp/oocService';
import {
  getEditAnchorMonthIdx,
  ncAgencyForFacilityCot,
} from 'screens/FDPScreen/components/ProvisionalForecastV2/Edit/editStore';

type FcColorType = 'new' | 'modified' | 'removed' | 'unchanged';

// Same new/modified/removed/unchanged rule used by the MAS DP / NC DP forecast
// grids (see updateForecastValue below) — shared by the Prov MAS / Prov NC
// panels so their edited cells get identical color coding.
const computeFcColorType = (newValue: number, originalValue: number): FcColorType => {
  if (newValue === 0 && originalValue > 0) return 'removed';
  if (originalValue === 0 && newValue > 0) return 'new';
  if (newValue !== originalValue) return 'modified';
  return 'unchanged';
};

// True when an AnnualMarketSales FacilityCOT belongs to the given ProvNC
// agency bucket ('VA' | 'DOD'). Reuses the canonical VA/DoD split from
// ProvisionalForecastV2 (ncAgencyForFacilityCot returns 'VA' | 'DoD') and
// normalises casing to OOC's 'VA' | 'DOD' agency literals.
const agencyMatchesFacility = (facilityCot: string, agency: string): boolean =>
  ncAgencyForFacilityCot(facilityCot).toUpperCase() === agency.toUpperCase();

/**
 * provForecastData / loadingProvForecastNDCs identity key. An NDC can have
 * BOTH an active ProvMAS and an active ProvNC provisional forecast for the
 * same planning period simultaneously — keying purely on ndc11 would make
 * the two grids read/write the same store slot and stomp on each other.
 */
export const provDataKey = (ndc11: string, tab: 'ProvMAS' | 'ProvNC'): string =>
  `${ndc11}::${tab}`;

export type OOCScreenView =
  | 'dashboard'
  | 'create'
  | 'edit'
  | 'communicate'
  | 'sc-review'
  | 'history';

export class OOCStore {
  // ─── Navigation ─────────────────────────────────────────────
  currentScreen: OOCScreenView = 'dashboard';

  // ─── Dashboard State ────────────────────────────────────────
  submissions: OOCSubmission[] = [];
  dashboardCounts: OOCDashboardCounts = {
    all: 0,
    draft: 0,
    communicated: 0,
    scReviewing: 0,
    history: 0,
  };
  isLoading: boolean = false;
  searchQuery: string = '';
  searchResults: OOCSubmission[] | null = null;
  isSearching: boolean = false;
  searchSuggestions: string[] = [];
  isFetchingSuggestions: boolean = false;
  statusFilter: OOCStatus | 'all' = 'all';
  reasonFilter: OOCReasonType | 'all' = 'all';
  contractTypeFilter: OOCTabType | 'all' = 'all';
  dateFilter: OOCDateFilter = 'This Month';
  activeCardFilter: OOCStatus | 'all' = 'all';

  // ─── Create / Edit State ────────────────────────────────────
  currentSubmission: OOCSubmission | null = null;
  activeTab: OOCTabType = 'MAS';
  ndcSearchQuery: string = '';
  ndcSearchResults: OOCNDCSearchResult[] = [];
  isNDCSearching: boolean = false;
  isSaving: boolean = false;
  isSendingEmail: boolean = false;
  savingSubmissionId: string | null = null;
  isSubmissionDirty: boolean = false;
  validationResult: OOCValidationResult | null = null;
  loadingBaselineNDCs: string[] = [];
  // IDs of adjustments whose reason field is empty (populated on failed communicate — Save Draft does not require a reason)
  missingReasonIds: string[] = [];

  // ─── Provisional Forecast Grid State ────────────────────────
  provForecastData: Record<string, OOCProvForecastData> = {};
  loadingProvForecastNDCs: string[] = [];
  // Shared NC settings (apply to all ProvNC NDCs in the current edit session)
  provNcMonthPcts: [number, number, number] = [50, 75, 90];
  provNcThreshold: number = 15;

  // ─── History State ──────────────────────────────────────────
  historyData: OOCIntegrationSummary[] = [];
  isHistoryLoading: boolean = false;
  historyDetailSubmission: OOCSubmission | null = null;
  isHistoryDetailLoading: boolean = false;
  historyMonth: number | null = null;
  historyYear: number | null = null; // null = all history (no date filter)

  constructor() {
    makeAutoObservable(this);
  }

  // ═══════════════════════════════════════════════════════════
  // Computed / Derived
  // ═══════════════════════════════════════════════════════════

  get filteredSubmissions(): OOCSubmission[] {
    return this.submissions.filter((sub) => {
      const statusMatch =
        this.statusFilter === 'all' || sub.status === this.statusFilter;
      const reasonMatch =
        this.reasonFilter === 'all' || sub.reason === this.reasonFilter;
      const contractTypeMatch =
        this.contractTypeFilter === 'all' ||
        sub.contractTypes.includes(this.contractTypeFilter);
      return statusMatch && reasonMatch && contractTypeMatch;
    });
  }

  get currentMASAdjustments(): OOCForecastAdjustment[] {
    return this.currentSubmission?.adjustments.filter((a) => a.tab === 'MAS') ?? [];
  }

  get currentNCAdjustments(): OOCForecastAdjustment[] {
    return this.currentSubmission?.adjustments.filter((a) => a.tab === 'NC') ?? [];
  }

  get currentProvMASAdjustments(): OOCForecastAdjustment[] {
    return this.currentSubmission?.adjustments.filter((a) => a.tab === 'ProvMAS') ?? [];
  }

  get currentProvNCAdjustments(): OOCForecastAdjustment[] {
    return this.currentSubmission?.adjustments.filter((a) => a.tab === 'ProvNC') ?? [];
  }

  get tabCounts(): Record<OOCTabType, number> {
    return {
      MAS: this.currentMASAdjustments.length,
      NC: this.currentNCAdjustments.length,
      ProvMAS: this.currentProvMASAdjustments.length,
      ProvNC: this.currentProvNCAdjustments.length,
    };
  }

  get tabsWithMissingReasons(): Set<OOCTabType> {
    if (!this.currentSubmission || this.missingReasonIds.length === 0) return new Set();
    const tabs = new Set<OOCTabType>();
    for (const adj of this.currentSubmission.adjustments) {
      if (this.missingReasonIds.includes(adj.id)) tabs.add(adj.tab);
    }
    return tabs;
  }

  get activeTabAdjustments(): OOCForecastAdjustment[] {
    switch (this.activeTab) {
      case 'MAS': return this.currentMASAdjustments;
      case 'NC': return this.currentNCAdjustments;
      case 'ProvMAS': return this.currentProvMASAdjustments;
      case 'ProvNC': return this.currentProvNCAdjustments;
    }
  }

  get referenceDataForActiveTab(): OOCForecastAdjustment[] {
    return this.activeTabAdjustments.filter((a) => a.referenceData);
  }

  // ═══════════════════════════════════════════════════════════
  // Actions - Navigation
  // ═══════════════════════════════════════════════════════════

  setScreen = (screen: OOCScreenView) => {
    this.currentScreen = screen;
  };

  navigateToDashboard = () => {
    this.currentScreen = 'dashboard';
    this.currentSubmission = null;
    this.activeTab = 'MAS';
    this.ndcSearchQuery = '';
    this.ndcSearchResults = [];
    this.validationResult = null;
    this.isSubmissionDirty = false;
    this.provForecastData = {};
    this.loadingProvForecastNDCs = [];
    this.provNcMonthPcts = [50, 75, 90];
    this.provNcThreshold = 15;
  };

  cancelAndSave = async () => {
    if (!this.currentSubmission || !this.isSubmissionDirty) {
      this.navigateToDashboard();
      return;
    }

    const submission = this.currentSubmission;

    runInAction(() => {
      this.savingSubmissionId = submission.id;
      this.isSubmissionDirty = false;
      this.currentScreen = 'dashboard';
      this.currentSubmission = null;
      this.activeTab = 'MAS';
      this.ndcSearchQuery = '';
      this.ndcSearchResults = [];
      this.validationResult = null;
    });

    try {
      await Promise.all([
        oocService.saveSubmission(submission),
        oocService.saveAdjustments(submission.id, submission.adjustments),
      ]);
    } catch {
      toast.error("We couldn't auto-save your changes. Please save this draft manually so you don't lose your work.");
    } finally {
      runInAction(() => {
        this.savingSubmissionId = null;
      });
      this.fetchSubmissions();
    }
  };

  // ═══════════════════════════════════════════════════════════
  // Actions - Dashboard
  // ═══════════════════════════════════════════════════════════

  fetchSubmissions = async () => {
    this.isLoading = true;
    try {
      const [subs, counts] = await Promise.all([
        oocService.getSubmissions(),
        oocService.getDashboardCounts(),
      ]);
      runInAction(() => {
        this.submissions = subs;
        this.dashboardCounts = counts;
        this.isLoading = false;
      });
    } catch (_) {
      runInAction(() => {
        this.isLoading = false;
      });
      toast.error("We couldn't load the OOC submissions list. Please refresh the page or try again.");
    }
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
    if (this.searchResults !== null) {
      this.searchResults = null;
    }
    if (!query) {
      this.searchSuggestions = [];
    }
  };

  performSearch = async (username?: string) => {
    const query = this.searchQuery.trim();
    if (!query) {
      this.searchResults = null;
      return;
    }
    this.isSearching = true;
    try {
      const results = await oocService.searchSubmissions(query, username);
      runInAction(() => {
        this.searchResults = results;
        this.isSearching = false;
      });
    } catch {
      runInAction(() => {
        this.isSearching = false;
        toast.error("We couldn't complete that search. Please try again.");
      });
    }
  };

  fetchSuggestions = async (query: string, username: string, signal?: AbortSignal) => {
    if (query.trim().length < 2) {
      this.searchSuggestions = [];
      return;
    }
    this.isFetchingSuggestions = true;
    try {
      const suggestions = await oocService.fetchSuggestions(query, username, signal);
      runInAction(() => {
        this.searchSuggestions = suggestions;
        this.isFetchingSuggestions = false;
      });
    } catch {
      // If the request was aborted a new one is already in flight — leave the loading state alone.
      if (signal?.aborted) return;
      runInAction(() => {
        this.isFetchingSuggestions = false;
      });
    }
  };

  clearSearch = () => {
    this.searchQuery = '';
    this.searchResults = null;
    this.searchSuggestions = [];
  };

  setStatusFilter = (status: OOCStatus | 'all') => {
    this.statusFilter = status;
    this.activeCardFilter = status;
  };

  setReasonFilter = (reason: OOCReasonType | 'all') => {
    this.reasonFilter = reason;
  };

  setContractTypeFilter = (contractType: OOCTabType | 'all') => {
    this.contractTypeFilter = contractType;
  };

  setDateFilter = (filter: OOCDateFilter) => {
    this.dateFilter = filter;
  };

  setActiveCardFilter = (filter: OOCStatus | 'all') => {
    this.activeCardFilter = filter;
    this.statusFilter = filter;
  };

  // ═══════════════════════════════════════════════════════════
  // Actions - Create / Edit
  // ═══════════════════════════════════════════════════════════

  createNewSubmission = async (submittedBy: string, planningPeriod: string) => {
    this.isLoading = true;
    try {
      const submission = await oocService.createSubmission(submittedBy, planningPeriod);
      runInAction(() => {
        this.currentSubmission = submission;
        this.currentScreen = 'create';
        this.activeTab = 'MAS';
        this.isLoading = false;
      });
      toast.success(`${submission.id} created as Draft`);
    } catch (_) {
      runInAction(() => {
        this.isLoading = false;
      });
      toast.error("We couldn't create a new OOC submission. Please try again.");
    }
  };

  openSubmissionForEdit = async (id: string) => {
    this.isLoading = true;
    try {
      const submission = await oocService.getSubmissionById(id);
      runInAction(() => {
        this.currentSubmission = submission;
        this.activeTab = 'MAS';
        this.isLoading = false;
        this.isSubmissionDirty = false;
        if (submission?.status === 'draft') {
          this.currentScreen = 'create';
        } else if (submission?.status === 'communicated') {
          this.currentScreen = 'communicate';
        }

        // Populate provForecastData from adjustments that carry facilityRows/varianceRows
        if (submission) {
          const newProvData: Record<string, OOCProvForecastData> = {};
          for (const adj of submission.adjustments) {
            if ((adj.tab === 'ProvMAS' || adj.tab === 'ProvNC') &&
                (adj.facilityRows?.length || adj.varianceRows?.length)) {
              newProvData[provDataKey(adj.ndc11, adj.tab)] = {
                ndc11: adj.ndc11,
                contractType: adj.tab === 'ProvMAS' ? 'MAS' : 'NC',
                facilityRows: adj.facilityRows ?? [],
                varianceRows: adj.varianceRows ?? [],
                marketData: adj.marketData ?? [],
                selectedPackSizes: adj.selectedPackSizes ?? [],
              };
            }
            // Initialize NC settings from the first ProvNC adjustment found
            if (adj.tab === 'ProvNC' && adj.ncMonthPcts?.length === 3) {
              this.provNcMonthPcts = adj.ncMonthPcts as [number, number, number];
              this.provNcThreshold = adj.ncThreshold ?? 15;
            }
          }
          this.provForecastData = newProvData;
        }
      });
    } catch (_) {
      runInAction(() => {
        this.isLoading = false;
      });
      toast.error("We couldn't open this OOC submission. Please try again.");
    }
  };

  setActiveTab = (tab: OOCTabType) => {
    this.activeTab = tab;
  };

  setOOCReason = (reason: OOCReasonType) => {
    if (this.currentSubmission) {
      this.currentSubmission = { ...this.currentSubmission, reason };
    }
  };

  updateAdjustmentReason = (adjustmentId: string, reason: OOCReasonType) => {
    if (!this.currentSubmission) return;
    const adjustments = this.currentSubmission.adjustments.map((a) =>
      a.id === adjustmentId ? { ...a, reason } : a
    );
    this.currentSubmission = { ...this.currentSubmission, adjustments };
    this.isSubmissionDirty = true;
    this.missingReasonIds = this.missingReasonIds.filter((id) => id !== adjustmentId);
  };

  // ─── NDC Search ─────────────────────────────────────────────

  setNDCSearchQuery = (query: string) => {
    this.ndcSearchQuery = query;
  };

  searchNDCs = async (signal?: AbortSignal) => {
    if (!this.ndcSearchQuery.trim()) {
      this.ndcSearchResults = [];
      return;
    }
    this.isNDCSearching = true;
    try {
      const results = await oocService.searchNDCs(this.ndcSearchQuery, signal);
      runInAction(() => {
        this.ndcSearchResults = results;
        this.isNDCSearching = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.isNDCSearching = false;
      });
      // Re-throw for aborted requests so callers can distinguish cancellation from real errors
      if (signal?.aborted) throw error;
    }
  };

  addBulkNDCsByMolecule = async (ndcs: OOCNDCSearchResult[], selectedTab: OOCTabType) => {
    for (const ndc of ndcs) {
      await this.addNDCToCurrentSubmission(ndc, selectedTab);
    }
  };

  setLoadingBaseline = (ndc11s: string[]) => {
    runInAction(() => {
      this.loadingBaselineNDCs = [...this.loadingBaselineNDCs, ...ndc11s];
    });
  };

  fetchAndApplyBaseline = async (ndc11s: string[]) => {
    try {
      const baseline = await oocService.fetchBaselineValues(ndc11s);
      runInAction(() => {
        if (!this.currentSubmission) return;
        this.currentSubmission = {
          ...this.currentSubmission,
          adjustments: this.currentSubmission.adjustments.map((adj) => {
            const data = baseline[adj.ndc11];
            if (!data) return adj;
            return {
              ...adj,
              forecasts: adj.forecasts.map((cell) => ({
                ...cell,
                value: data.forecast[cell.period] ?? cell.value,
                colorType: 'new' as const,
              })),
            };
          }),
        };
        this.loadingBaselineNDCs = this.loadingBaselineNDCs.filter((n) => !ndc11s.includes(n));
        this.isSubmissionDirty = true;
      });
    } catch {
      runInAction(() => {
        this.loadingBaselineNDCs = this.loadingBaselineNDCs.filter((n) => !ndc11s.includes(n));
      });
      toast.error("We couldn't load the saved forecast values for this drug. Please try again.");
    }
  };

  // ─── Provisional Forecast Actions ───────────────────────────

  fetchProvForecastParams = async (ndc11: string, tab: 'ProvMAS' | 'ProvNC') => {
    const key = provDataKey(ndc11, tab);
    runInAction(() => {
      this.loadingProvForecastNDCs = [...this.loadingProvForecastNDCs, key];
    });
    try {
      const contractType = tab === 'ProvNC' ? 'NC' : 'MAS';
      const data = await oocService.fetchProvForecastParams(ndc11, contractType);
      runInAction(() => {
        this.provForecastData = {
          ...this.provForecastData,
          [key]: {
            ...data,
            marketData: data.marketData ?? [],
            selectedPackSizes: data.selectedPackSizes ?? [],
          },
        };
        this.loadingProvForecastNDCs = this.loadingProvForecastNDCs.filter(k => k !== key);
      });
    } catch {
      runInAction(() => {
        this.loadingProvForecastNDCs = this.loadingProvForecastNDCs.filter(k => k !== key);
      });
      toast.error(`We couldn't load the provisional forecast details for drug ${ndc11}. Please try again.`);
    }
  };

  updateProvFacilityPct = (ndc11: string, rowIdx: number, monthIdx: number, val: number) => {
    const key = provDataKey(ndc11, 'ProvMAS');
    const existing = this.provForecastData[key];
    if (!existing) return;
    const rows = existing.facilityRows.map((r, i) => {
      if (i !== rowIdx) return r;
      const pcts = [...r.monthlyPercentages];
      const clamped = Math.max(0, Math.min(100, val));
      pcts[monthIdx] = clamped;
      // Same anchor-month cascade rule as the canonical Provisional Forecast
      // MAS grid: editing the anchor month copies its value to every later
      // month. Anchor is 0 for MTF/TMOP/VISN (single editable month), 3 for
      // every other facility (CMOP, "VA Facility", etc.).
      const anchor = getEditAnchorMonthIdx(r.cot);
      if (monthIdx === anchor) {
        for (let m = anchor + 1; m < 15; m++) pcts[m] = clamped;
      }
      // originalMonthlyPercentages is captured once (on the first edit) and
      // never overwritten afterward, same as previousValue/originalNceVolume.
      const originalPcts = r.originalMonthlyPercentages ?? r.monthlyPercentages;
      const colorTypes = [...(r.monthlyColorTypes ?? pcts.map(() => 'unchanged' as const))];
      colorTypes[monthIdx] = computeFcColorType(clamped, originalPcts[monthIdx] ?? 0);
      if (monthIdx === anchor) {
        for (let m = anchor + 1; m < 15; m++) {
          colorTypes[m] = computeFcColorType(clamped, originalPcts[m] ?? 0);
        }
      }
      return {
        ...r,
        monthlyPercentages: pcts,
        originalMonthlyPercentages: originalPcts,
        monthlyColorTypes: colorTypes,
      };
    });
    this.provForecastData = { ...this.provForecastData, [key]: { ...existing, facilityRows: rows } };
    this.isSubmissionDirty = true;
  };

  toggleProvFacility = (ndc11: string, rowIdx: number) => {
    const key = provDataKey(ndc11, 'ProvMAS');
    const existing = this.provForecastData[key];
    if (!existing) return;
    const rows = existing.facilityRows.map((r, i) =>
      i === rowIdx ? { ...r, isSelected: !r.isSelected } : r,
    );
    // 100% rule: if ≤2 CMOPs selected, auto-set those CMOPs to 100%
    const selectedCMOPs = rows.filter(r => r.cot === 'CMOP' && r.isSelected);
    const finalRows =
      selectedCMOPs.length > 0 && selectedCMOPs.length <= 2
        ? rows.map(r => {
            if (r.cot !== 'CMOP' || !r.isSelected) return r;
            const originalPcts = r.originalMonthlyPercentages ?? r.monthlyPercentages;
            const forcedPcts = Array(15).fill(100);
            const colorTypes = forcedPcts.map((v, m) => computeFcColorType(v, originalPcts[m] ?? 0));
            return {
              ...r,
              monthlyPercentages: forcedPcts,
              originalMonthlyPercentages: originalPcts,
              monthlyColorTypes: colorTypes,
            };
          })
        : rows;
    this.provForecastData = { ...this.provForecastData, [key]: { ...existing, facilityRows: finalRows } };
    this.isSubmissionDirty = true;
  };

  updateProvNCE = (ndc11: string, agency: string, val: number) => {
    const key = provDataKey(ndc11, 'ProvNC');
    const existing = this.provForecastData[key];
    if (!existing) return;
    const varianceRows = existing.varianceRows.map(r => {
      if (r.agency !== agency) return r;
      // Same color logic as updateForecastValue: originalNceVolume is
      // captured once (on the first edit) and never overwritten afterward.
      const originalNceVolume =
        r.originalNceVolume !== undefined && r.originalNceVolume !== null
          ? r.originalNceVolume
          : r.nceVolume;
      const colorType = computeFcColorType(val, originalNceVolume);
      return { ...r, nceVolume: val, originalNceVolume, colorType };
    });
    this.provForecastData = { ...this.provForecastData, [key]: { ...existing, varianceRows } };
    this.isSubmissionDirty = true;
  };

  updateProvNcSettings = (monthPcts: [number, number, number], threshold: number) => {
    this.provNcMonthPcts = monthPcts.map(v => Math.max(0, Math.min(100, v))) as [number, number, number];
    this.provNcThreshold = threshold;
    this.isSubmissionDirty = true;
  };

  /**
   * Toggle a pack size on/off for the ProvMAS/ProvNC grid — mirrors
   * ProvisionalForecastV2's editStore.togglePackSelected. `selected` and
   * `used_in_calc` are kept mirrored (OOC has no Packout dual-selector),
   * and recomputes annualVolSU / historical12MVolume from marketData so
   * the grid stays in sync the moment the operator flips a chip.
   *
   * Refuses to uncheck the last calc-selected pack — leaving every
   * `used_in_calc=false` would zero out the math.
   */
  toggleProvPackSelected = (ndc11: string, tab: 'ProvMAS' | 'ProvNC', packsize: string) => {
    const key = provDataKey(ndc11, tab);
    const existing = this.provForecastData[key];
    if (!existing) return;
    const list = existing.selectedPackSizes ?? [];
    const entry = list.find(p => p.packsize === packsize);
    const isUnchecking = entry?.used_in_calc === true;
    if (isUnchecking) {
      const otherSelectedCount = list.filter(
        p => p.packsize !== packsize && p.used_in_calc,
      ).length;
      if (otherSelectedCount === 0) {
        toast.warn('At least one pack size must remain selected.');
        return;
      }
    }
    const nextList: SelectedPackSize[] = entry
      ? list.map(p => {
          if (p.packsize !== packsize) return p;
          const nextOn = !p.used_in_calc;
          return { ...p, selected: nextOn, used_in_calc: nextOn };
        })
      : [...list, { packsize, selected: true, used_in_calc: true }];

    this.provForecastData = {
      ...this.provForecastData,
      [key]: this.recomputeProvVolumes({ ...existing, selectedPackSizes: nextList }),
    };
    this.isSubmissionDirty = true;
  };

  /**
   * Re-derives annualVolSU (ProvMAS) / historical12MVolume (ProvNC) from
   * the calc-selected packs in `marketData` — same join keys as the
   * canonical Provisional Forecast recompute: facility_cot === facilityName
   * for MAS, agency bucket of facility_cot === row.agency for NC. Leaves
   * values untouched if zero packs are calc'd (shouldn't happen — the
   * toggle guard above refuses to reach that state — but matches the
   * canonical store's defensive no-op).
   */
  private recomputeProvVolumes = (data: OOCProvForecastData): OOCProvForecastData => {
    const calcPacks = new Set(
      data.selectedPackSizes.filter(p => p.selected && p.used_in_calc).map(p => p.packsize),
    );
    if (calcPacks.size === 0) return data;

    const sumFor = (predicate: (row: (typeof data.marketData)[number]) => boolean): number =>
      Math.floor(
        data.marketData
          .filter(row => calcPacks.has(row.packsize) && predicate(row))
          .reduce((sum, row) => sum + (parseFloat(row.annual_market) || 0), 0),
      );

    const facilityRows = data.facilityRows.map(row => ({
      ...row,
      annualVolSU: sumFor(mkt => mkt.facility_cot === row.facilityName),
    }));

    const varianceRows = data.varianceRows.map(row => ({
      ...row,
      historical12MVolume: sumFor(mkt => agencyMatchesFacility(mkt.facility_cot, row.agency)),
    }));

    return { ...data, facilityRows, varianceRows };
  };

  addNDCToCurrentSubmission = async (ndc: OOCNDCSearchResult, selectedTab?: OOCTabType) => {
    if (!this.currentSubmission) return;
    const targetTab: OOCTabType = selectedTab ?? 'MAS';
    const tabLabel = targetTab === 'MAS' ? 'MAS DP' : targetTab === 'NC' ? 'NC DP' : targetTab === 'ProvMAS' ? 'Prov. MAS' : 'Prov. NC';
    // Check if already added under this SAME tab — an NDC can legitimately
    // exist under both a "DP" tab (MAS/NC, from an unconstrained forecast)
    // and a "Prov" tab (ProvMAS/ProvNC, from a provisional forecast)
    // simultaneously, so the dupe check must be scoped to (ndc11, tab), not
    // ndc11 alone.
    const exists = this.currentSubmission.adjustments.some(
      (a) => a.ndc11 === ndc.ndc11 && a.tab === targetTab
    );
    if (exists) {
      toast.warn(`${ndc.ndc11} is already in this OOC submission's ${tabLabel} tab`);
      return;
    }
    try {
      const adj = await oocService.addNDCToSubmission(
        this.currentSubmission.id,
        ndc,
        targetTab
      );

      // Backend stores the adjustment with empty forecasts until baseline is fetched.
      // Seed the 15-period skeleton here so fetchAndApplyBaseline has slots to fill.
      if (adj.forecasts.length === 0) {
        adj.forecasts = generate15MonthPeriods().map((period) => ({
          period,
          value: 0,
          previousValue: null,
          colorType: 'new' as const,
        }));
      }

      runInAction(() => {
        if (this.currentSubmission) {
          this.currentSubmission = {
            ...this.currentSubmission,
            adjustments: [...this.currentSubmission.adjustments, adj],
            ndcCount: this.currentSubmission.adjustments.length + 1,
            contractTypes: Array.from(
              new Set([...this.currentSubmission.contractTypes, targetTab])
            ),
          };
          this.activeTab = targetTab;
          this.isSubmissionDirty = true;
        }
      });
      toast.success(`${ndc.ndc11} added to ${tabLabel} tab`);
    } catch {
      toast.error("We couldn't add that NDC to the submission. Please try again.");
    }
  };

  removeAdjustment = async (adjustmentId: string) => {
    if (!this.currentSubmission) return;
    try {
      await oocService.removeAdjustment(this.currentSubmission.id, adjustmentId);
      runInAction(() => {
        if (this.currentSubmission) {
          const updated = this.currentSubmission.adjustments.filter(
            (a) => a.id !== adjustmentId
          );
          this.currentSubmission = {
            ...this.currentSubmission,
            adjustments: updated,
            ndcCount: updated.length,
          };
        }
      });
      toast.success('NDC removed from submission');
    } catch {
      toast.error("We couldn't remove that NDC from the submission. Please try again.");
    }
  };

  // ─── Forecast Cell Editing ──────────────────────────────────

  updateForecastValue = (
    adjustmentId: string,
    periodIndex: number,
    newValue: number | null
  ) => {
    if (!this.currentSubmission) return;
    const adjIdx = this.currentSubmission.adjustments.findIndex(
      (a) => a.id === adjustmentId
    );
    if (adjIdx === -1) return;

    const adj = { ...this.currentSubmission.adjustments[adjIdx] };
    const forecasts = [...adj.forecasts];
    const cell = { ...forecasts[periodIndex] };

    // Track the original value (the value before any user edits).
    // previousValue is set once and never overwritten on subsequent edits.
    const originalValue = cell.previousValue !== null ? cell.previousValue : cell.value;

    // If this is the first edit, capture the original value
    if (cell.previousValue === null && cell.value !== newValue) {
      cell.previousValue = cell.value;
    }

    cell.value = newValue;

    // ─── Color logic based on business rules ─────────────────
    // Green (new):     First-ever forecast (original was null/0, new value > 0)
    // Orange (modified): Value changed from a previous non-zero value (±20% or any change)
    // Red (removed):   Forecast zeroed out (new value is 0, original was > 0)
    // White (unchanged): Value matches original or cell is empty
    if (newValue === null) {
      // Empty cell
      cell.colorType = 'unchanged';
    } else if (newValue === 0 && originalValue !== null && originalValue > 0) {
      // Forecast zeroed out → removed (red)
      cell.colorType = 'removed';
    } else if ((originalValue === null || originalValue === 0) && newValue > 0) {
      // First-ever forecast entry → new (green)
      cell.colorType = 'new';
    } else if (originalValue !== null && newValue !== originalValue) {
      // Value changed from a previous non-zero value → modified (orange)
      cell.colorType = 'modified';
    } else {
      // Value matches original → unchanged (white)
      cell.colorType = 'unchanged';
      // If value is back to original, clear previousValue
      if (newValue === originalValue) {
        cell.previousValue = null;
      }
    }

    forecasts[periodIndex] = cell;
    adj.forecasts = forecasts;

    const adjustments = [...this.currentSubmission.adjustments];
    adjustments[adjIdx] = adj;
    this.currentSubmission = { ...this.currentSubmission, adjustments };
    this.isSubmissionDirty = true;
  };

  updateNotes = (adjustmentId: string, notes: string) => {
    if (!this.currentSubmission) return;
    const adjustments = this.currentSubmission.adjustments.map((a) =>
      a.id === adjustmentId ? { ...a, notes } : a
    );
    this.currentSubmission = { ...this.currentSubmission, adjustments };
    this.isSubmissionDirty = true;
  };

  updateProbability = (adjustmentId: string, probability: ProbabilityLevel) => {
    if (!this.currentSubmission) return;
    const adjustments = this.currentSubmission.adjustments.map((a) =>
      a.id === adjustmentId ? { ...a, probability } : a
    );
    this.currentSubmission = { ...this.currentSubmission, adjustments };
    this.isSubmissionDirty = true;
  };

  // ─── Save / Communicate ─────────────────────────────────────

  saveDraft = async () => {
    if (!this.currentSubmission) return;

    // Save Draft persists partial/incomplete submissions — Reason is only
    // required when communicating to SC, not when saving a draft.
    this.isSaving = true;
    try {
      // Split adjustments: regular (MAS/NC) vs provisional (ProvMAS/ProvNC)
      const regularAdjs = this.currentSubmission.adjustments.filter(
        a => a.tab === 'MAS' || a.tab === 'NC',
      );
      const provAdjs = this.currentSubmission.adjustments.filter(
        a => a.tab === 'ProvMAS' || a.tab === 'ProvNC',
      );

      // Build provisional save payload: merge store provForecastData into each adjustment
      const provPayload = provAdjs.map(adj => {
        const gridData = this.provForecastData[provDataKey(adj.ndc11, adj.tab as 'ProvMAS' | 'ProvNC')];
        return {
          id: adj.id,
          notes: adj.notes,
          reason: adj.reason,
          probability: adj.probability,
          ncMonthPcts: this.provNcMonthPcts,
          ncThreshold: this.provNcThreshold,
          facilityRows: gridData?.facilityRows ?? adj.facilityRows ?? [],
          varianceRows: gridData?.varianceRows ?? adj.varianceRows ?? [],
          selectedPackSizes: gridData?.selectedPackSizes ?? adj.selectedPackSizes ?? [],
        };
      });

      const saves: Promise<unknown>[] = [
        oocService.saveSubmission(this.currentSubmission),
      ];
      if (regularAdjs.length > 0) {
        saves.push(oocService.saveAdjustments(this.currentSubmission.id, regularAdjs));
      }
      if (provPayload.length > 0) {
        saves.push(oocService.saveProvAdjustments(this.currentSubmission.id, provPayload));
      }
      await Promise.all(saves);

      runInAction(() => {
        this.isSaving = false;
        this.isSubmissionDirty = false;
      });
      toast.success('Draft saved successfully');
    } catch {
      runInAction(() => {
        this.isSaving = false;
      });
      toast.error("We couldn't save your changes. Please try again before leaving this page.");
    }
  };

  communicateToSC = async () => {
    if (!this.currentSubmission) return;

    // Block communication if no NDCs have been added
    if (this.currentSubmission.adjustments.length === 0) {
      toast.warn('At least one NDC must be added before communicating to SC.');
      return;
    }

    const missing = this.currentSubmission.adjustments.filter((a) => !a.reason);
    if (missing.length > 0) {
      this.missingReasonIds = missing.map((a) => a.id);
      this.activeTab = missing[0].tab;
      toast.warn(
        `${missing.length} NDC${missing.length > 1 ? 's are' : ' is'} missing a reason. Please select a reason for every NDC before communicating.`
      );
      return;
    }
    this.missingReasonIds = [];

    this.isSaving = true;
    try {
      // Flush current MobX state to the DB before communicating.
      // The backend reads OOCForecastCell rows when writing FCT rows on communicate,
      // so forecast values must be persisted first or they'll be missing from the email.
      const regularAdjs = this.currentSubmission.adjustments.filter(
        a => a.tab === 'MAS' || a.tab === 'NC',
      );
      const provAdjs = this.currentSubmission.adjustments.filter(
        a => a.tab === 'ProvMAS' || a.tab === 'ProvNC',
      );
      const preSaves: Promise<unknown>[] = [
        oocService.saveSubmission(this.currentSubmission),
      ];
      if (regularAdjs.length > 0) {
        preSaves.push(oocService.saveAdjustments(this.currentSubmission.id, regularAdjs));
      }
      if (provAdjs.length > 0) {
        const provPayload = provAdjs.map(adj => {
          const gridData = this.provForecastData[provDataKey(adj.ndc11, adj.tab as 'ProvMAS' | 'ProvNC')];
          return {
            id: adj.id,
            notes: adj.notes,
            reason: adj.reason,
            probability: adj.probability,
            ncMonthPcts: this.provNcMonthPcts,
            ncThreshold: this.provNcThreshold,
            facilityRows: gridData?.facilityRows ?? adj.facilityRows ?? [],
            varianceRows: gridData?.varianceRows ?? adj.varianceRows ?? [],
            selectedPackSizes: gridData?.selectedPackSizes ?? adj.selectedPackSizes ?? [],
          };
        });
        preSaves.push(oocService.saveProvAdjustments(this.currentSubmission.id, provPayload));
      }
      await Promise.all(preSaves);

      // Validate first
      const validation = await oocService.validateSubmission(this.currentSubmission.id);
      if (validation.warnings.length > 0) {
        runInAction(() => {
          this.validationResult = validation;
        });
        // Show warnings but allow proceed
        validation.warnings.forEach((w) => toast.warn(w.message));
      }

      const updated = await oocService.communicateToSC(this.currentSubmission.id);
      runInAction(() => {
        this.currentSubmission = updated;
        this.currentScreen = 'communicate';
        this.isSaving = false;
      });
      toast.success(`${updated.id} communicated to SC.`);
    } catch {
      runInAction(() => {
        this.isSaving = false;
      });
      toast.error("We couldn't send this submission to Supply Chain. Please try again.");
    }
  };

  sendEmail = async () => {
    if (!this.currentSubmission) return;
    this.isSendingEmail = true;
    try {
      await oocService.sendEmail(this.currentSubmission.id);
      runInAction(() => {
        this.isSendingEmail = false;
      });
      toast.success('Email sent successfully to SC, Purchasing, PPS, BD, and CSE.');
    } catch {
      runInAction(() => {
        this.isSendingEmail = false;
      });
      toast.error("We couldn't send the notification email. Please try again.");
    }
  };

  markAsCommunicated = async () => {
    if (!this.currentSubmission) return;
    toast.success(
      `${this.currentSubmission.id} marked as Communicated. SC and Purchasing have been notified.`
    );
  };

  // ═══════════════════════════════════════════════════════════
  // Actions - History
  // ═══════════════════════════════════════════════════════════

  fetchHistory = async () => {
    this.isHistoryLoading = true;
    try {
      const data = await oocService.getHistory(
        this.historyYear,
        this.historyMonth ?? undefined,
      );
      runInAction(() => {
        this.historyData = data;
        this.isHistoryLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isHistoryLoading = false;
      });
      toast.error("We couldn't load the OOC history. Please try again.");
    }
  };

  setHistoryFilter = (month: number | null, year: number | null) => {
    this.historyMonth = month;
    this.historyYear = year;
    this.fetchHistory();
  };

  fetchHistoryDetail = async (oocId: string) => {
    this.isHistoryDetailLoading = true;
    this.historyDetailSubmission = null;
    try {
      const data = await oocService.getHistoricalSubmissionById(oocId);
      runInAction(() => {
        this.historyDetailSubmission = data;
        this.isHistoryDetailLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isHistoryDetailLoading = false;
      });
      toast.error("We couldn't load the details for this OOC submission. Please try again.");
    }
  };

  closeHistoryDetail = () => {
    this.historyDetailSubmission = null;
  };

  // ═══════════════════════════════════════════════════════════
  // Reset
  // ═══════════════════════════════════════════════════════════

  resetStore = () => {
    this.currentScreen = 'dashboard';
    this.submissions = [];
    this.dashboardCounts = { all: 0, draft: 0, communicated: 0, scReviewing: 0, history: 0 };
    this.isLoading = false;
    this.searchQuery = '';
    this.searchResults = null;
    this.searchSuggestions = [];
    this.statusFilter = 'all';
    this.reasonFilter = 'all';
    this.contractTypeFilter = 'all';
    this.dateFilter = 'This Month';
    this.activeCardFilter = 'all';
    this.currentSubmission = null;
    this.activeTab = 'MAS';
    this.ndcSearchQuery = '';
    this.ndcSearchResults = [];
    this.isNDCSearching = false;
    this.isSaving = false;
    this.isSendingEmail = false;
    this.validationResult = null;
    this.historyData = [];
    this.isHistoryLoading = false;
    this.historyDetailSubmission = null;
    this.isHistoryDetailLoading = false;
    this.historyMonth = null;
    this.historyYear = null;
    this.provForecastData = {};
    this.loadingProvForecastNDCs = [];
    this.provNcMonthPcts = [50, 75, 90];
    this.provNcThreshold = 15;
  };
}
